import { Shield, Lock, Eye, Trash2, Mail, Phone, Globe, MessageCircle, Instagram, Megaphone, FileText, Users, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";

const PrivacyPolicy = () => {
  const lastUpdated = "February 3, 2026";
  const companyName = "Vendas Group";
  const appName = "Huggy Bits Connect";
  const contactEmail = "privacidade@vendas.group";
  const deletionEmail = "exclusao@vendas.group";
  const websiteUrl = "https://huggy-bits-connect.lovable.app";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground py-12">
        <div className="container mx-auto px-4">
          <Button variant="ghost" asChild className="mb-4 text-primary-foreground hover:text-primary-foreground/80">
            <Link to="/auth">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Link>
          </Button>
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-10 w-10" />
            <h1 className="text-3xl md:text-4xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-primary-foreground/80 text-lg">
            {appName} by {companyName}
          </p>
          <p className="text-primary-foreground/60 text-sm mt-2">
            Last updated: {lastUpdated}
          </p>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Introduction */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Eye className="h-6 w-6 text-primary" />
            Introduction
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            {companyName} ("we", "us", or "our") operates the {appName} platform. 
            This Privacy Policy explains how we collect, use, disclose, and protect 
            your personal information when you use our services, including integrations 
            with Meta platforms (Facebook, Instagram, WhatsApp).
          </p>
          <p className="text-muted-foreground leading-relaxed">
            By using {appName}, you agree to the collection and use of information 
            in accordance with this policy. If you do not agree with this policy, 
            please do not use our services.
          </p>
        </section>

        <Separator className="my-8" />

        {/* Information We Collect */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <FileText className="h-6 w-6 text-primary" />
            Information We Collect
          </h2>
          
          <div className="space-y-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Account Information
                </h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Name, email address, and phone number</li>
                  <li>Company/organization name</li>
                  <li>Login credentials (encrypted)</li>
                  <li>Profile preferences and settings</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-primary" />
                  WhatsApp Business API Data
                </h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>WhatsApp Business Account information</li>
                  <li>Phone numbers of contacts who message your business</li>
                  <li>Message content and conversation history</li>
                  <li>Message delivery status and timestamps</li>
                  <li>Media files shared in conversations (images, documents, audio)</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-3">
                  <strong>Purpose:</strong> Enable customer communication management, 
                  lead tracking, and automated responses through your WhatsApp Business account.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Instagram className="h-5 w-5 text-primary" />
                  Instagram Direct Messaging Data
                </h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Instagram Business/Creator Account information</li>
                  <li>Instagram user IDs of people who message your account</li>
                  <li>Direct message content and conversation history</li>
                  <li>Profile information of users who interact with your account (name, username, profile picture)</li>
                  <li>Story replies and reactions</li>
                  <li>Media shared in conversations</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-3">
                  <strong>Purpose:</strong> Provide a unified inbox for customer service, 
                  enable quick responses to customer inquiries, and track customer 
                  interactions across your Instagram presence.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Megaphone className="h-5 w-5 text-primary" />
                  Meta Lead Ads Data (leads_retrieval)
                </h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Lead form submissions from Facebook/Instagram ads</li>
                  <li>Contact information provided in lead forms (name, email, phone)</li>
                  <li>Custom question responses from lead forms</li>
                  <li>Campaign and ad set identifiers</li>
                  <li>Submission timestamps</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-3">
                  <strong>Purpose:</strong> Automatically import leads from your Meta ad 
                  campaigns into the CRM for immediate follow-up, sales pipeline management, 
                  and campaign performance tracking.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Globe className="h-5 w-5 text-primary" />
                  Meta Ads Performance Data
                </h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Ad account information and identifiers</li>
                  <li>Campaign, ad set, and ad performance metrics</li>
                  <li>Spend, impressions, clicks, and conversions</li>
                  <li>Audience insights (aggregated, non-personal)</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-3">
                  <strong>Purpose:</strong> Display advertising performance dashboards 
                  and integrate ad metrics with sales pipeline data.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <Separator className="my-8" />

        {/* How We Use Your Information */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Lock className="h-6 w-6 text-primary" />
            How We Use Your Information
          </h2>
          
          <div className="grid gap-4">
            <div className="p-4 border rounded-lg">
              <h3 className="font-semibold mb-2">Service Delivery</h3>
              <p className="text-muted-foreground text-sm">
                Process and manage customer communications across WhatsApp and Instagram, 
                import leads from ad campaigns, and provide CRM functionality.
              </p>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h3 className="font-semibold mb-2">Customer Relationship Management</h3>
              <p className="text-muted-foreground text-sm">
                Track lead interactions, manage sales pipelines, assign leads to team members, 
                and automate follow-up workflows.
              </p>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h3 className="font-semibold mb-2">Analytics & Reporting</h3>
              <p className="text-muted-foreground text-sm">
                Generate performance reports, track conversion metrics, and provide 
                insights to optimize your sales and marketing efforts.
              </p>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h3 className="font-semibold mb-2">Service Improvement</h3>
              <p className="text-muted-foreground text-sm">
                Analyze usage patterns to improve our platform, fix bugs, and develop 
                new features that better serve your needs.
              </p>
            </div>
          </div>
        </section>

        <Separator className="my-8" />

        {/* Meta Platform Compliance */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Globe className="h-6 w-6 text-primary" />
            Meta Platform Data Usage
          </h2>
          
          <div className="bg-primary/5 p-6 rounded-lg border border-primary/20">
            <h3 className="font-semibold mb-3">Compliance with Meta Platform Terms</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Our use of data obtained through Meta APIs (Facebook, Instagram, WhatsApp) 
              is governed by the Meta Platform Terms. We commit to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground text-sm space-y-2">
              <li>Only requesting permissions necessary for our stated functionality</li>
              <li>Not selling, licensing, or purchasing any data obtained from Meta</li>
              <li>Not using Meta data for surveillance or discriminatory purposes</li>
              <li>Deleting all user data upon request or when no longer needed</li>
              <li>Implementing appropriate security measures to protect Meta data</li>
              <li>Not transferring Meta data to third parties without user consent</li>
            </ul>
          </div>

          <div className="mt-6 space-y-4">
            <h3 className="font-semibold">Permissions We Request</h3>
            
            <div className="grid gap-3">
              <div className="p-3 bg-muted/50 rounded-lg">
                <code className="text-sm font-mono text-primary">instagram_business_basic</code>
                <p className="text-sm text-muted-foreground mt-1">
                  Access basic Instagram Business account information to identify your connected accounts.
                </p>
              </div>
              
              <div className="p-3 bg-muted/50 rounded-lg">
                <code className="text-sm font-mono text-primary">instagram_business_manage_messages</code>
                <p className="text-sm text-muted-foreground mt-1">
                  Read and send Instagram Direct messages on behalf of your business to manage customer conversations.
                </p>
              </div>
              
              <div className="p-3 bg-muted/50 rounded-lg">
                <code className="text-sm font-mono text-primary">leads_retrieval</code>
                <p className="text-sm text-muted-foreground mt-1">
                  Retrieve lead form submissions from your Facebook/Instagram ad campaigns for CRM import.
                </p>
              </div>
              
              <div className="p-3 bg-muted/50 rounded-lg">
                <code className="text-sm font-mono text-primary">whatsapp_business_messaging</code>
                <p className="text-sm text-muted-foreground mt-1">
                  Send and receive WhatsApp messages through your connected WhatsApp Business account.
                </p>
              </div>
              
              <div className="p-3 bg-muted/50 rounded-lg">
                <code className="text-sm font-mono text-primary">ads_read, ads_management</code>
                <p className="text-sm text-muted-foreground mt-1">
                  Read advertising performance data and manage ad configurations for reporting purposes.
                </p>
              </div>
            </div>
          </div>
        </section>

        <Separator className="my-8" />

        {/* Data Retention */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Data Retention</h2>
          <p className="text-muted-foreground mb-4">
            We retain your personal information for as long as necessary to provide 
            our services and fulfill the purposes described in this policy:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2">
            <li><strong>Account data:</strong> Retained while your account is active</li>
            <li><strong>Message history:</strong> Retained for up to 24 months or until deletion request</li>
            <li><strong>Lead data:</strong> Retained until manually deleted by you or your organization</li>
            <li><strong>Analytics data:</strong> Aggregated data may be retained indefinitely</li>
          </ul>
        </section>

        <Separator className="my-8" />

        {/* Data Deletion */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Trash2 className="h-6 w-6 text-destructive" />
            Data Deletion & Your Rights
          </h2>
          
          <Card className="border-destructive/30 bg-destructive/5">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-lg mb-4">How to Request Data Deletion</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="bg-destructive/10 p-2 rounded-full">
                    <Mail className="h-4 w-4 text-destructive" />
                  </div>
                  <div>
                    <p className="font-medium">Email Request</p>
                    <p className="text-sm text-muted-foreground">
                      Send a deletion request to{" "}
                      <a href={`mailto:${deletionEmail}`} className="text-primary underline">
                        {deletionEmail}
                      </a>
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="bg-destructive/10 p-2 rounded-full">
                    <Globe className="h-4 w-4 text-destructive" />
                  </div>
                  <div>
                    <p className="font-medium">In-App Deletion</p>
                    <p className="text-sm text-muted-foreground">
                      Use the account settings in {appName} to delete your data
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="bg-destructive/10 p-2 rounded-full">
                    <Shield className="h-4 w-4 text-destructive" />
                  </div>
                  <div>
                    <p className="font-medium">Meta Data Deletion Callback</p>
                    <p className="text-sm text-muted-foreground">
                      If you connected via Facebook/Instagram Login, you can request 
                      deletion directly through your Meta account settings. We will 
                      receive and process the request automatically.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-background rounded-lg">
                <p className="text-sm">
                  <strong>Processing Time:</strong> Deletion requests are processed within 
                  30 days. You will receive a confirmation code to track your request at{" "}
                  <a href={`${websiteUrl}/data-deletion-status`} className="text-primary underline">
                    {websiteUrl}/data-deletion-status
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="mt-6">
            <h3 className="font-semibold mb-3">Your Rights Include:</h3>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your data</li>
              <li><strong>Portability:</strong> Request your data in a portable format</li>
              <li><strong>Objection:</strong> Object to certain types of data processing</li>
              <li><strong>Withdraw Consent:</strong> Withdraw previously given consent</li>
            </ul>
          </div>
        </section>

        <Separator className="my-8" />

        {/* GDPR/CCPA */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">International Users (GDPR/CCPA)</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-3">European Union Users (GDPR)</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  If you are located in the European Union, you have additional rights 
                  under the General Data Protection Regulation:
                </p>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  <li>Right to lodge a complaint with a supervisory authority</li>
                  <li>Right to restrict processing</li>
                  <li>Right to data portability</li>
                  <li>Right not to be subject to automated decision-making</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-3">California Users (CCPA)</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  If you are a California resident, you have rights under the California 
                  Consumer Privacy Act:
                </p>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  <li>Right to know what personal information is collected</li>
                  <li>Right to know if personal information is sold or disclosed</li>
                  <li>Right to say no to the sale of personal information</li>
                  <li>Right to non-discrimination for exercising rights</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-3">
                  <strong>We do not sell your personal information.</strong>
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <Separator className="my-8" />

        {/* Security */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Lock className="h-6 w-6 text-primary" />
            Data Security
          </h2>
          <p className="text-muted-foreground mb-4">
            We implement industry-standard security measures to protect your data:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2">
            <li>Encryption of data in transit (TLS/SSL) and at rest</li>
            <li>Secure authentication mechanisms</li>
            <li>Regular security audits and vulnerability assessments</li>
            <li>Access controls and employee training</li>
            <li>Compliance with Meta Platform security requirements</li>
          </ul>
        </section>

        <Separator className="my-8" />

        {/* Third-Party Sharing */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Third-Party Sharing</h2>
          <p className="text-muted-foreground mb-4">
            We may share your information with:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2">
            <li><strong>Service Providers:</strong> Cloud hosting, analytics, and support services</li>
            <li><strong>Meta Platforms:</strong> As required for API functionality</li>
            <li><strong>Legal Requirements:</strong> When required by law or legal process</li>
            <li><strong>Business Transfers:</strong> In connection with mergers or acquisitions</li>
          </ul>
          <p className="text-muted-foreground mt-4">
            <strong>We never sell your personal data to third parties.</strong>
          </p>
        </section>

        <Separator className="my-8" />

        {/* Contact */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Mail className="h-6 w-6 text-primary" />
            Contact Us
          </h2>
          <p className="text-muted-foreground mb-4">
            If you have questions about this Privacy Policy or our data practices, 
            please contact us:
          </p>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <a href={`mailto:${contactEmail}`} className="text-primary underline">
                {contactEmail}
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-muted-foreground" />
              <a href={websiteUrl} className="text-primary underline" target="_blank" rel="noopener noreferrer">
                {websiteUrl}
              </a>
            </div>
          </div>
        </section>

        {/* Policy Updates */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Policy Updates</h2>
          <p className="text-muted-foreground">
            We may update this Privacy Policy from time to time. We will notify you 
            of any material changes by posting the new policy on this page and updating 
            the "Last updated" date. We encourage you to review this policy periodically.
          </p>
        </section>

        {/* Footer */}
        <footer className="text-center text-sm text-muted-foreground pt-8 border-t">
          <p>© {new Date().getFullYear()} {companyName}. All rights reserved.</p>
          <p className="mt-2">
            <a href="/termos" className="text-primary underline">Terms of Service</a>
            {" · "}
            <a href="/data-deletion-status" className="text-primary underline">Data Deletion Status</a>
          </p>
        </footer>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
